import React, { useMemo, useState } from 'react';
import { Box, Button, useTheme, CircularProgress } from '@mui/material';
import { useGetAssetBreakdownQuery, useExportAssetTransactionsMutation } from '@/services/dashboardApi';
import { useSelector } from 'react-redux';
import { UserRole } from '@/types/user';
import { RootState } from '@/store/store';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import excelIcon from '../../assets/excel-icon.svg';

interface AssetTransactionsChartProps {
    amcId?: string;
}

export const AssetTransactionsChart: React.FC<AssetTransactionsChartProps> = ({ amcId }) => {
    const [activePeriod, setActivePeriod] = useState<string>('1W');
    const theme = useTheme();
    const role = useSelector((state: RootState) => state.user.role) as UserRole | undefined;
    
    const periods = [
        { label: '1W', value: '1W' },
        { label: '1M', value: '1M' },
        { label: '6M', value: '6M' },
        { label: '1Y', value: '1Y' },
        { label: 'All', value: 'All' },
    ];

    // Build API parameters with optional AMC filter
    const apiParams: any = {
        type: activePeriod as '1W' | '1M' | '6M' | '1Y' | 'All',
    };
    if (amcId) {
        apiParams.amcId = amcId;
    }

    const { data } = useGetAssetBreakdownQuery(apiParams);
    const [exportAssetTransactions, { isLoading: isDownloading }] = useExportAssetTransactionsMutation();

    const handleDownload = async (e?: React.MouseEvent) => {
        e?.preventDefault();
        e?.stopPropagation();

        try {
            const blob = await exportAssetTransactions({
                amcId,
                type: activePeriod as '1W' | '1M' | '6M' | '1Y' | 'All',
            }).unwrap();

            // Create download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.style.display = 'none';
            link.href = url;
            link.download = 'asset-transactions.xlsx';

            document.body.appendChild(link);

            // Use requestAnimationFrame to ensure proper download
            requestAnimationFrame(() => {
                link.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(link);
            });
        } catch (error) {
            console.error('Download failed:', error);
            alert('Failed to download file. Please try again.');
        }
    };

    // Only AMC Admin and AMC User can view this chart
    const isAuthorized = role === UserRole.AMC_ADMIN || role === UserRole.AMC;
    if (!isAuthorized) return null;

    const rows = Array.isArray(data) ? data : [];

    const categories = rows.map((r) => r.assetName || '-');

    const chartTitle = useMemo<Highcharts.TitleOptions>(() => ({
        text: 'Asset Wise Transactions',
        align: 'left' as const,
        style: {
            ...theme.typography.h3,
            fontWeight: theme.typography.h3.fontWeight?.toString(),
        },
    }), [theme.typography.h3]);

    const options: Highcharts.Options = {
        credits: { enabled: false },
        chart: { type: 'area' },
        title: chartTitle,
        xAxis: {
            categories,
            lineColor: theme.palette.divider,
            labels: { style: { fontSize: theme.typography.body2.fontSize?.toString() } },
        },
        yAxis: {
            title: { text: '' },
            gridLineDashStyle: 'Dash',
            gridLineColor: theme.palette.divider,
        },
        legend: {
            enabled: true,
            align: 'left',
            verticalAlign: 'top',
            itemStyle: {
                color: theme.typography.subtitle1.color,
                fontSize: theme.typography.subtitle1.fontSize?.toString(),
                fontWeight: theme.typography.subtitle1.fontWeight?.toString(),
                fontFamily: 'Roboto',
            },
            symbolRadius: 50,
            symbolHeight: 10,
            symbolWidth: 10,
            itemDistance: 30,
            itemMarginBottom: 5,
        },
        plotOptions: {
            area: { fillOpacity: 0.1, marker: { radius: 4, lineWidth: 2 } },
            column: { borderRadius: 2 },
        },
        series: [
            {
                type: 'area',
                name: 'Total Transactions',
                data: rows.map((r) => r.totalTransactions || 0),
                color: theme.palette.areaChart.totalTransactions,
            },
            {
                type: 'area',
                name: 'Successful Transactions',
                data: rows.map((r) => r.successCount || 0),
                color: theme.palette.areaChart.successTranscations,
                marker: { symbol: 'circle' },
            },
            {
                type: 'column',
                name: 'Failed Transactions',
                data: rows.map((r) => r.failureCount || 0),
                color: theme.palette.areaChart.failedTranscations,
            },
            {
                type: 'column',
                name: 'Pending Transactions',
                data: rows.map((r) => r.pendingCount || 0),
                color: (theme.palette as any).areaChart?.pendingTranscations || theme.palette.warning.main,
            },
        ],
    };

    return (
        <Box
            sx={{
                backgroundColor: 'white',
                borderRadius: '10px',
                p: 3,
                flex: 1,
                minWidth: '300px',
                display: 'flex',
                flexDirection: 'column',
                gap: 2
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {periods.map((period) => (
                        <Button
                            key={period.value}
                            variant={activePeriod === period.value ? 'contained' : 'outlined'}
                            size="small"
                            onClick={() => setActivePeriod(period.value)}
                            sx={{
                                minWidth: '40px',
                                padding: '4px 12px',
                                fontSize: '0.75rem',
                                fontWeight: activePeriod === period.value ? 600 : 400,
                                textTransform: 'none',
                                '&.MuiButton-contained': {
                                    backgroundColor: theme.palette.primary.main,
                                    color: theme.palette.primary.contrastText,
                                    '&:hover': {
                                        backgroundColor: theme.palette.primary.dark,
                                    },
                                },
                            }}
                        >
                            {period.label}
                        </Button>
                    ))}
                </Box>
                <Button
                    variant="outlined"
                    onClick={handleDownload}
                    disabled={isDownloading}
                    startIcon={
                        isDownloading ? (
                            <CircularProgress size={16} color="inherit" />
                        ) : (
                            <img src={excelIcon} alt="Excel" style={{ width: 20, height: 20 }} />
                        )
                    }
                    sx={{
                        fontSize: '0.75rem',
                        textTransform: 'none',
                        color: theme.palette.success.main,
                        borderColor: theme.palette.success.main,
                        '&:hover': {
                            borderColor: theme.palette.success.dark,
                            backgroundColor: 'rgba(0, 200, 83, 0.04)',
                        },
                    }}
                >
                    {isDownloading ? 'Downloading...' : 'Export'}
                </Button>
            </Box>
            <Box sx={{ flex: 1 }}>
                <HighchartsReact highcharts={Highcharts} options={options} />
            </Box>
        </Box>
    );
};


